import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable/index';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

/**
 * Link / unlink OAuth identities (Google, Apple) to the currently signed-in
 * account so the user can sign in with multiple providers later.
 *
 * Uses Supabase's `linkIdentity` / `unlinkIdentity` for connected identities,
 * and the Lovable Cloud helper to drive the OAuth popup when starting a link.
 */
type ProviderKey = 'google' | 'apple';

const PROVIDERS: { key: ProviderKey; label: string }[] = [
  { key: 'google', label: 'Google' },
  { key: 'apple', label: 'Apple' },
];

export function LinkedAccounts() {
  const [identities, setIdentities] = useState<Array<{ id: string; provider: string }>>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<ProviderKey | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.auth.getUserIdentities();
    if (!error && data?.identities) {
      setIdentities(data.identities.map((i: any) => ({ id: i.identity_id, provider: i.provider })));
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const linked = (p: ProviderKey) => identities.some((i) => i.provider === p);

  async function link(p: ProviderKey) {
    setBusy(p);
    try {
      // `linkIdentity` is the canonical way to attach a new provider to the
      // already-signed-in user. It starts an OAuth redirect like a sign-in.
      const { data, error } = await supabase.auth.linkIdentity({
        provider: p,
        options: { redirectTo: window.location.origin },
      } as any);
      if (error) {
        // Fall back to the Lovable helper for projects where linkIdentity isn't
        // available — it still creates an identity on completion.
        const result = await lovable.auth.signInWithOAuth(p, { redirect_uri: window.location.origin });
        if (result.error) throw result.error;
        if (!result.redirected) await refresh();
      } else if (!(data as any)?.url) {
        await refresh();
      }
    } catch (err: any) {
      toast.error(err?.message || `Could not link ${p}`);
    } finally {
      setBusy(null);
    }
  }

  async function unlink(p: ProviderKey) {
    const identity = identities.find((i) => i.provider === p);
    if (!identity) return;
    if (identities.length <= 1) {
      toast.error('Add another sign-in method before removing this one.');
      return;
    }
    setBusy(p);
    try {
      const { error } = await supabase.auth.unlinkIdentity({
        identity_id: identity.id,
        provider: p,
      } as any);
      if (error) throw error;
      toast.success(`${p[0].toUpperCase() + p.slice(1)} unlinked`);
      await refresh();
    } catch (err: any) {
      toast.error(err?.message || `Could not unlink ${p}`);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-2">
      {loading ? (
        <p className="text-[11px] text-muted-foreground font-body">Loading…</p>
      ) : (
        PROVIDERS.map((p) => {
          const isLinked = linked(p.key);
          return (
            <div key={p.key} className="flex items-center justify-between rounded-lg border border-border bg-secondary/40 px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="text-xs text-foreground font-body">{p.label}</span>
                {isLinked && (
                  <span className="text-[9px] uppercase tracking-wide text-primary font-body">Linked</span>
                )}
              </div>
              <button
                onClick={() => (isLinked ? unlink(p.key) : link(p.key))}
                disabled={busy === p.key}
                className={`inline-flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-body transition-colors ${isLinked ? 'border border-border text-muted-foreground hover:text-foreground' : 'bg-primary text-primary-foreground hover:bg-primary/90'}`}
              >
                {busy === p.key && <Loader2 className="h-3 w-3 animate-spin" />}
                {isLinked ? 'Unlink' : 'Link'}
              </button>
            </div>
          );
        })
      )}
      <p className="text-[10px] text-muted-foreground font-body">
        Link Google or Apple to sign in faster next time. You must keep at least one sign-in method.
      </p>
    </div>
  );
}
